import { useMemo, useState } from "react";
import { useData } from "@/store/DataContext";
import MovementRow from "@/components/feature/MovementRow";
import EmptyState from "@/components/base/EmptyState";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import type { TimelineEntry } from "@/lib/metrics";

type Filter = "all" | TimelineEntry["kind"];

const filters: { key: Filter; label: string }[] = [
  { key: "all", label: "Todos" },
  { key: "purchase", label: "Compras" },
  { key: "sale", label: "Ventas" },
  { key: "expense", label: "Gastos" },
  { key: "return", label: "Devoluciones" },
];

const deleteMessage: Record<TimelineEntry["kind"], string> = {
  purchase: "Se eliminará esta compra y el stock que agregó al inventario. Esta acción no se puede deshacer.",
  sale: "Se eliminará esta venta y el stock vendido volverá al inventario. Esta acción no se puede deshacer.",
  expense: "Se eliminará este gasto. Esta acción no se puede deshacer.",
  return: "Se eliminará esta devolución. Esta acción no se puede deshacer.",
};

export default function Movimientos() {
  const { timeline, loading, deletePurchase, deleteSale, deleteExpense, deleteReturn } = useData();
  const [filter, setFilter] = useState<Filter>("all");
  const [pending, setPending] = useState<TimelineEntry | null>(null);

  const filtered = useMemo(
    () => (filter === "all" ? timeline : timeline.filter((e) => e.kind === filter)),
    [timeline, filter],
  );

  const handleConfirm = async () => {
    if (!pending) return;
    const entry = pending;
    setPending(null);
    if (entry.kind === "purchase") await deletePurchase(entry.id);
    else if (entry.kind === "sale") await deleteSale(entry.id);
    else if (entry.kind === "expense") await deleteExpense(entry.id);
    else if (entry.kind === "return") await deleteReturn(entry.id);
  };

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
      </div>
    );
  }

  return (
    <div className="fr-animate space-y-5">
      <div>
        <h1 className="text-[22px] font-bold text-foreground-950">Movimientos</h1>
        <p className="mt-0.5 text-[13px] text-foreground-500">Compras, ventas, gastos y devoluciones</p>
      </div>

      <div className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-1 no-scrollbar">
        {filters.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`flex-none rounded-full px-4 py-2 text-[13px] font-medium transition-colors ${
              filter === f.key
                ? "bg-primary-500 text-background-50"
                : "bg-background-50 text-foreground-600 border border-background-200/70"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon="ri-exchange-line"
          title="No hay movimientos"
          description="Cuando registres compras, ventas o gastos aparecerán aquí."
        />
      ) : (
        <div className="divide-y divide-background-200 overflow-hidden rounded-3xl border border-background-200/70 bg-background-50 shadow-card">
          {filtered.map((entry) => (
            <MovementRow key={entry.id} entry={entry} onDelete={() => setPending(entry)} />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!pending}
        title="Eliminar movimiento"
        message={pending ? deleteMessage[pending.kind] : ""}
        onCancel={() => setPending(null)}
        onConfirm={handleConfirm}
      />
    </div>
  );
}